/**
 * Adapter hook that turns the workspace's `useMentionSearch` into the
 * `MentionSuggestionConfig` that `MarkdownEditor` expects.
 *
 * Any consumer of `MarkdownEditor` can opt the @-mention popup on with
 * a single line:
 *
 *     const mentionSuggestion = useEditorMentionSuggestion();
 *     <MarkdownEditor ... mentionSuggestion={mentionSuggestion} />
 *
 * The hook owns the `(query, active)` React state, calls
 * `useMentionSearch` to fetch entities, and memoises the suggestion
 * object so MarkdownEditor's `useEditor` doesn't re-install the plugin.
 *
 * Read-only editors can still call this hook — the popup never opens
 * unless the user types `@` into an editable surface, so there's no
 * runtime cost beyond an idle `useState`.
 */
import { useMemo, useState } from "react";

import { useMentionSearch } from "../../../modules/episodes/hooks/useMentionSearch";
import type { MentionSuggestionConfig } from "./useMentionEditorUI";

export function useEditorMentionSuggestion(): MentionSuggestionConfig {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(false);
  const { results, isLoading } = useMentionSearch(query, active);

  return useMemo<MentionSuggestionConfig>(
    () => ({
      results,
      isLoading,
      onQueryChange: (q, a): void => {
        setQuery(q);
        setActive(a);
      },
    }),
    [results, isLoading],
  );
}
